import { MutationTree } from 'vuex';
import {
    AccountInventoryResultDiffState,
    AccountInventoryResultDiff,
    AccountInventoryResultDiffBasicInformation,
    AccountInventoryResultDiffChangedAccountDetails,
    AccountInventoryResultDiffDetails,
} from './types';
import * as types from './mutation-types';
import { ErrorDetail } from '../../types';

// エラーをクリアする
const resetErrors = (state: AccountInventoryResultDiffState): void => {
    const searchState = state.search;
    searchState.error = false;
    searchState.errorCode = undefined;
    searchState.errorMsg = undefined;

    const showState = state.show;
    showState.error = false;
    showState.errorCode = undefined;
    showState.errorMsg = undefined;

    const changedAccountsState = state.changedAccounts;
    changedAccountsState.error = false;
    changedAccountsState.errorCode = undefined;
    changedAccountsState.errorMsg = undefined;

    const addedAccountsState = state.addedAccounts;
    addedAccountsState.error = false;
    addedAccountsState.errorCode = undefined;
    addedAccountsState.errorMsg = undefined;

    const deletedAccountsState = state.deletedAccounts;
    deletedAccountsState.error = false;
    deletedAccountsState.errorCode = undefined;
    deletedAccountsState.errorMsg = undefined;

    const csvState = state.csv;
    csvState.error = false;
    csvState.errorTitle = undefined;
    csvState.errorDetails = undefined;
};

export const mutations: MutationTree<AccountInventoryResultDiffState> = {
    // 棚卸結果差分一覧検索系
    [types.SEARCH_START](state) {
        resetErrors(state);
        const searchState = state.search;
        searchState.isProcessing = true;
        searchState.isFetchSuccess = false;
    },

    [types.SEARCH_SUCCESS](state, payload: { count: number; inventoryResultDiffs: Array<AccountInventoryResultDiff> }) {
        state.inventoryResultDiffCount = payload.count;
        state.inventoryResultDiffs = payload.inventoryResultDiffs;

        const searchState = state.search;
        searchState.isProcessing = false;
        searchState.isFetchSuccess = true;
    },

    [types.SEARCH_FAILURE](state, payload: { code: string; message: string }) {
        const searchState = state.search;
        searchState.errorCode = payload.code;
        searchState.errorMsg = payload.message;
        searchState.error = true;
        searchState.isProcessing = false;
        searchState.isFetchSuccess = false;
    },

    // 棚卸結果差分詳細取得系
    [types.SHOW_START](state) {
        resetErrors(state);
        const showState = state.show;
        showState.isProcessing = true;
        showState.isFetchSuccess = false;
    },

    [types.SHOW_SUCCESS](state, payload: AccountInventoryResultDiffBasicInformation) {
        const showState = state.show;
        state.inventoryResultDiffBasicInformation = payload;

        showState.isProcessing = false;
        showState.isFetchSuccess = true;
    },

    [types.SHOW_FAILURE](state, payload: { code: string; message: string }) {
        const showState = state.show;
        showState.errorCode = payload.code;
        showState.errorMsg = payload.message;
        showState.error = true;
        showState.isProcessing = false;
        showState.isFetchSuccess = false;
    },

    // 棚卸結果差分詳細 アカウント属性変更あり検索系
    [types.CHANGED_ACCOUNTS_START](state) {
        resetErrors(state);
        const changedAccountsState = state.changedAccounts;
        changedAccountsState.isProcessing = true;
        changedAccountsState.isFetchSuccess = false;
    },

    [types.CHANGED_ACCOUNTS_SUCCESS](
        state,
        payload: { count: number; diffs: Array<AccountInventoryResultDiffChangedAccountDetails> }
    ) {
        state.inventoryResultDiffChangedAccountDetailsCount = payload.count;
        state.inventoryResultDiffChangedAccountDetails = payload.diffs;

        const changedAccountsState = state.changedAccounts;
        changedAccountsState.isProcessing = false;
        changedAccountsState.isFetchSuccess = true;
    },

    [types.CHANGED_ACCOUNTS_FAILURE](state, payload: { code: string; message: string }) {
        const changedAccountsState = state.changedAccounts;
        changedAccountsState.errorCode = payload.code;
        changedAccountsState.errorMsg = payload.message;
        changedAccountsState.error = true;
        changedAccountsState.isProcessing = false;
        changedAccountsState.isFetchSuccess = false;
    },

    // 棚卸結果差分詳細 アカウント追加検索系
    [types.ADDED_ACCOUNTS_START](state) {
        resetErrors(state);
        const addedAccountsState = state.addedAccounts;
        addedAccountsState.isProcessing = true;
        addedAccountsState.isFetchSuccess = false;
    },

    [types.ADDED_ACCOUNTS_SUCCESS](state, payload: { count: number; diffs: Array<AccountInventoryResultDiffDetails> }) {
        state.inventoryResultDiffAddedAccountDetailsCount = payload.count;
        state.inventoryResultDiffAddedAccountDetails = payload.diffs;

        const addedAccountsState = state.addedAccounts;
        addedAccountsState.isProcessing = false;
        addedAccountsState.isFetchSuccess = true;
    },

    [types.ADDED_ACCOUNTS_FAILURE](state, payload: { code: string; message: string }) {
        const addedAccountsState = state.addedAccounts;
        addedAccountsState.errorCode = payload.code;
        addedAccountsState.errorMsg = payload.message;
        addedAccountsState.error = true;
        addedAccountsState.isProcessing = false;
        addedAccountsState.isFetchSuccess = false;
    },

    // 棚卸結果差分詳細 アカウント削除検索系
    [types.DELETED_ACCOUNTS_START](state) {
        resetErrors(state);
        const deletedAccountsState = state.deletedAccounts;
        deletedAccountsState.isProcessing = true;
        deletedAccountsState.isFetchSuccess = false;
    },

    [types.DELETED_ACCOUNTS_SUCCESS](
        state,
        payload: { count: number; diffs: Array<AccountInventoryResultDiffDetails> }
    ) {
        state.inventoryResultDiffDeletedAccountDetailsCount = payload.count;
        state.inventoryResultDiffDeletedAccountDetails = payload.diffs;

        const deletedAccountsState = state.deletedAccounts;
        deletedAccountsState.isProcessing = false;
        deletedAccountsState.isFetchSuccess = true;
    },

    [types.DELETED_ACCOUNTS_FAILURE](state, payload: { code: string; message: string }) {
        const deletedAccountsState = state.deletedAccounts;
        deletedAccountsState.errorCode = payload.code;
        deletedAccountsState.errorMsg = payload.message;
        deletedAccountsState.error = true;
        deletedAccountsState.isProcessing = false;
        deletedAccountsState.isFetchSuccess = false;
    },

    // CSV出力系
    [types.CSV_OUTPUT_START](state) {
        resetErrors(state);
        const csvState = state.csv;
        csvState.isProcessing = true;
    },

    [types.CSV_OUTPUT_SUCCESS](state) {
        const csvState = state.csv;
        csvState.isProcessing = false;
    },

    [types.CSV_OUTPUT_FAILURE](state, payload: { errorTitle: string; errorDetails: Array<ErrorDetail> }) {
        const csvState = state.csv;
        csvState.errorTitle = payload.errorTitle;
        csvState.errorDetails = payload.errorDetails;
        csvState.error = true;
        csvState.isProcessing = false;
    },

    // FATALエラー系
    [types.FATAL_FAILURE](state) {
        const searchState = state.search;
        searchState.isProcessing = false;
        searchState.isFetchSuccess = false;
    },

    [types.CLEAR_ALL](state) {
        resetErrors(state);
        state.inventoryResultDiffs = undefined;
        state.inventoryResultDiffCount = 0;
    },
};
